/**
 * QcService — BQC5 pass/repair event log (iSyn2_QcScans). Pure append +
 * soft-undo, mirrors iSyn2_CutReadyScans; qcLedger.ts is what an outcome
 * MEANS (latest-wins, rework counting, the QC queue). Never writes
 * iSyn_WIPs — stage authority stays Excel-owned until a later Gate 3
 * (SCREENS-BQC3.md §7).
 */
import { createSharePointClient } from "./spClient";
import { MFG_SITE_URL } from "./mfgSite";
import { readByIdRanges } from "./idRangeRead";
import type { IQcScan, QcOutcome, RepairKind } from "../../modules/mfg-workcenters/qcLedger";
import { notifyPortalStale } from "../../modules/sewer-portal/portalStale";

export type { IQcScan, QcOutcome, RepairKind };

const QC_LIST = "iSyn2_QcScans";

/** Columns every live list already has. `returnedBy`/`returnedAt` and the
 * Sup Approval columns (`repairKind`/`remark`/`supApprovedBy`/`supApprovedAt`/
 * `supRejectedBy`/`supRejectedAt`) were added later (Isyn2ProvisioningService's
 * addMissingFields) and may not exist yet on a list provisioned before them —
 * SharePoint 400s the WHOLE $select if even one named column is missing, so
 * reads try the fullest select first and fall back a generation at a time
 * (see listQcScans), mirroring ReceivingService's identical
 * RECEIVING_CORE/SELECT split. */
const QC_CORE = "Id,wipId,qty,outcome,qcBy,qcAt,undoneBy,undoneAt";
const QC_SELECT_V2 = `${QC_CORE},returnedBy,returnedAt`;
const QC_SELECT_V3 = `${QC_SELECT_V2},repairKind,remark,supApprovedBy,supApprovedAt,supRejectedBy,supRejectedAt`;
const QC_SELECT_V4 = `${QC_SELECT_V3},syncedToWorkbookAt`;
/**
 * `sourceKey` — the QC userform row this scan was imported from, or absent on
 * a scan an operator recorded at the station.
 *
 * Provisioned in a SECOND deploy ("Add missing columns" on the Migration
 * Valve), so this generation must fall back like every one before it: naming
 * a column the list does not have 400s the WHOLE $select and takes out every
 * reader of the list at once. `qcSourceKeysReady()` is how the importer asks
 * whether the column is actually there before it writes anything.
 */
const QC_SELECT_V5 = `${QC_SELECT_V4},sourceKey`;

export class QcService {
  private readonly sp = createSharePointClient(MFG_SITE_URL);
  private types = new Map<string, string>();

  private async type(list: string): Promise<string> {
    const hit = this.types.get(list);
    if (hit) return hit;
    const r = await this.sp.raw<{ d: { ListItemEntityTypeFullName: string } }>(
      `/_api/web/lists/getbytitle('${list}')?$select=ListItemEntityTypeFullName`);
    this.types.set(list, r.d.ListItemEntityTypeFullName);
    return r.d.ListItemEntityTypeFullName;
  }

  private mapScan(r: Record<string, unknown>): IQcScan {
    return {
      spId: r.Id as number,
      wipId: String(r.wipId ?? "").trim(),
      qty: Number(r.qty ?? 0),
      outcome: (r.outcome as QcOutcome) === "repair" ? "repair" : "pass",
      qcBy: String(r.qcBy ?? ""),
      qcAt: String(r.qcAt ?? ""),
      undoneBy: r.undoneBy ? String(r.undoneBy) : undefined,
      undoneAt: r.undoneAt ? String(r.undoneAt) : undefined,
      returnedBy: r.returnedBy ? String(r.returnedBy) : undefined,
      returnedAt: r.returnedAt ? String(r.returnedAt) : undefined,
      repairKind: r.repairKind === "inhouse" ? "inhouse" : r.repairKind === "subcontractor" ? "subcontractor" : undefined,
      remark: r.remark ? String(r.remark) : undefined,
      supApprovedBy: r.supApprovedBy ? String(r.supApprovedBy) : undefined,
      supApprovedAt: r.supApprovedAt ? String(r.supApprovedAt) : undefined,
      supRejectedBy: r.supRejectedBy ? String(r.supRejectedBy) : undefined,
      supRejectedAt: r.supRejectedAt ? String(r.supRejectedAt) : undefined,
      syncedToWorkbookAt: r.syncedToWorkbookAt ? String(r.syncedToWorkbookAt) : undefined,
      sourceKey: r.sourceKey ? String(r.sourceKey) : undefined,
    };
  }

  /**
   * Set once a ranged read has failed AND the ordinary pager then succeeded —
   * the proof that it was the ranges and not the select that was at fault, so
   * later reads stop paying for an attempt that is known not to work here.
   */
  private rangedReadBroken = false;

  /**
   * One read of the whole ledger, in parallel Id ranges when it can.
   *
   * The ledger is 36,030 rows, which the pager reads as eight requests that
   * cannot overlap — eight round trips before BQC5 has anything to show, on
   * every cold open and every Refresh (Mikko, 2026-10-01). Ranged, it is two
   * waves. See idRangeRead.ts for why the ranges are safe and why the order is
   * unchanged.
   *
   * ANY failure falls back to the ordinary pager rather than being reported:
   * that read is the authority. In particular a select naming a column the list
   * does not have fails BOTH ways, and it is the pager's error that the
   * column-generation chain in `listQcScans` is waiting for.
   */
  private async read(select: string, onPage?: (rows: number) => void): Promise<IQcScan[]> {
    let rangedFailed = false;
    if (!this.rangedReadBroken) {
      try {
        const rows = await readByIdRanges(
          q => this.sp.getListItems<Record<string, unknown>>(QC_LIST, q),
          select, { onProgress: onPage });
        return rows.map(r => this.mapScan(r));
      } catch {
        rangedFailed = true;
      }
    }
    const rows = await this.sp.getListItems<Record<string, unknown>>(QC_LIST, { select, onPage });
    if (rangedFailed) this.rangedReadBroken = true;
    return rows.map(r => this.mapScan(r));
  }

  /**
   * Every scan row, undone ones included — the caller reduces to active.
   *
   * `onPage` reports the running row count as the pages land. The importer
   * uses it to put a real number in front of somebody waiting on a read of a
   * list this size; every other caller ignores it.
   */
  public async listQcScans(opts?: { onPage?: (rows: number) => void }): Promise<IQcScan[]> {
    try {
      return await this.read(QC_SELECT_V5, opts?.onPage);
    } catch (eKey) {
      // Quiet: until the valve step runs this is the NORMAL path, and a
      // console warning per read would train everyone to ignore the log. The
      // importer asks explicitly instead, and refuses rather than guessing.
      void eKey;
      return this.readV4(opts?.onPage);
    }
  }

  /** One WIP's scans only, fresh from SharePoint — a filtered query, not the 36,000-row ledger.
   * Throws when the list cannot filter on wipId (view threshold); the caller falls back. */
  public async listQcScansForWip(wip: string): Promise<IQcScan[]> {
    const rows = await this.sp.getListItems<Record<string, unknown>>(QC_LIST, {
      select: QC_SELECT_V4, filter: `wipId eq '${wip.replace(/'/g, "''")}'`, top: 500,
    });
    return rows.map(r => this.mapScan(r));
  }

  /**
   * Whether `iSyn2_QcScans` has the `sourceKey` column yet.
   *
   * The importer MUST consult this. Without the column every stored row reads
   * as keyless, so a re-run sees an empty list, plans ten thousand inserts and
   * doubles the ledger — the one failure the key exists to prevent.
   */
  public async qcSourceKeysReady(): Promise<boolean> {
    try {
      // singlePage IS THE WHOLE PROBE (2026-09-14). Without it `top: 1` is a
      // PAGE SIZE, not a limit, and this existence check paged the entire
      // 36,030-row ledger one row per HTTP request — 36,030 round trips, each
      // behind its own CORS preflight, in front of every preview and every
      // run. The import looked like it hung because it had. See
      // SPQueryOptions.singlePage, which says exactly this about probes.
      await this.sp.getListItems<Record<string, unknown>>(
        QC_LIST, { select: `Id,sourceKey`, top: 1, singlePage: true });
      return true;
    } catch {
      return false;
    }
  }

  private async readV4(onPage?: (rows: number) => void): Promise<IQcScan[]> {
    try {
      return await this.read(QC_SELECT_V4, onPage);
    } catch (e0) {
      console.warn("[QcService] scan read without syncedToWorkbookAt — Export to BQC5 cannot tell what it has already sent. Use \"Add missing columns\" on the Migration Valve.", e0);
      return this.readV3(onPage);
    }
  }

  private async readV3(onPage?: (rows: number) => void): Promise<IQcScan[]> {
    try {
      return await this.read(QC_SELECT_V3, onPage);
    } catch (e) {
      console.warn("[QcService] scan read without repairKind/remark/Sup Approval columns — use \"Add missing columns\" on the Migration Valve to add them.", e);
      try {
        return await this.read(QC_SELECT_V2, onPage);
      } catch (e2) {
        console.warn("[QcService] scan read without returnedBy/returnedAt columns — use \"Add missing columns\" on the Migration Valve to add them.", e2);
        return this.read(QC_CORE, onPage);
      }
    }
  }

  /**
   * Write one IMPORTED scan — a row transcribed from the QC userform, not an
   * event this app witnessed.
   *
   * Separate from `createQcScan` on purpose. That one stamps `qcAt` with the
   * clock and `qcBy` with whoever is signed in, which is exactly right for a
   * scan at the bench and exactly wrong here: this row's date and inspector
   * belong to the sheet, and inventing them would turn a transcription into a
   * claim that somebody inspected something at 2pm today.
   *
   * `assertBackfillTarget` is the caller's job, once per run, before the first
   * of these — see QcImportService.
   */
  public async createImportedQcScan(row: {
    sourceKey: string; wipId: string; qty: number; outcome: QcOutcome;
    qcBy: string; qcAt: string; remark?: string;
  }): Promise<number> {
    const type = await this.type(QC_LIST);
    const created = await this.sp.createItem<{ Id: number }>(QC_LIST, {
      __metadata: { type },
      Title: row.wipId,
      wipId: row.wipId,
      qty: row.qty,
      outcome: row.outcome,
      qcBy: row.qcBy,
      qcAt: row.qcAt,
      // repairKind stays null: `repairKindOf` reads absent as "subcontractor",
      // which is what a REPAIR-tab row is, and the sheet has no column that
      // says so. See qcWorkbookImport.ts.
      remark: row.outcome === "repair" ? (row.remark || null) : null,
      sourceKey: row.sourceKey,
    });
    return created.Id;
  }

  /** Bring an already-imported row back in line with the sheet. */
  public async updateImportedQcScan(spId: number, row: {
    wipId: string; qty: number; outcome: QcOutcome; qcAt: string; remark?: string;
  }): Promise<void> {
    const type = await this.type(QC_LIST);
    await this.sp.updateItem(QC_LIST, spId, {
      __metadata: { type },
      wipId: row.wipId,
      qty: row.qty,
      outcome: row.outcome,
      qcAt: row.qcAt,
      remark: row.outcome === "repair" ? (row.remark || null) : null,
    });
  }

  /** Records one QC outcome and returns it with its list id, so the station
   * can show the row (and undo it) without a full reload. `repairKind`/
   * `remark` are only meaningful when outcome is "repair" — see qcLedger's
   * RepairKind and the file header there. */
  public async createQcScan(
    row: { wipId: string; outcome: QcOutcome; qty?: number; repairKind?: RepairKind; remark?: string },
    qcBy: string
  ): Promise<IQcScan> {
    const type = await this.type(QC_LIST);
    const qcAt = new Date().toISOString();
    const isRepair = row.outcome === "repair";
    const created = await this.sp.createItem<{ Id: number }>(QC_LIST, {
      __metadata: { type },
      Title: row.wipId,
      wipId: row.wipId,
      qty: row.qty ?? null,
      outcome: row.outcome,
      qcBy,
      qcAt,
      repairKind: isRepair ? (row.repairKind ?? "subcontractor") : null,
      remark: isRepair ? (row.remark || null) : null,
    });
    notifyPortalStale();
    return {
      spId: created.Id, wipId: row.wipId, qty: row.qty ?? 0,
      outcome: row.outcome, qcBy, qcAt,
      repairKind: isRepair ? (row.repairKind ?? "subcontractor") : undefined,
      remark: isRepair ? (row.remark || undefined) : undefined,
    };
  }

  /** Soft-undo a mis-scan. The row keeps existing as history; it just stops
   * counting as this WIP's outcome everywhere. */
  public async undoQcScan(spId: number, by: string): Promise<void> {
    const type = await this.type(QC_LIST);
    await this.sp.updateItem(QC_LIST, spId, {
      __metadata: { type },
      undoneBy: by,
      undoneAt: new Date().toISOString(),
    });
  }

  /**
   * Marks a `repair` outcome as actually handed to the subcontractor — a
   * deliberate, separate action from recording the failure itself (see
   * sewerReturns.ts and Qc.tsx's "Return to Sewer" button). Only from this
   * moment does the round count toward being matched with a return scan.
   */
  public async markReturnedToSewer(spId: number, by: string): Promise<void> {
    const type = await this.type(QC_LIST);
    await this.sp.updateItem(QC_LIST, spId, {
      __metadata: { type },
      returnedBy: by,
      returnedAt: new Date().toISOString(),
    });
  }

  /** A supervisor signs off on an in-house repair's categorization — see
   * qcLedger's supApprovalStatusOf. Purely a review-layer write: the scan
   * already counted toward qcTally the moment it was recorded, so this
   * never touches outcome/qty. */
  public async approveInhouseRepair(spId: number, by: string): Promise<void> {
    const type = await this.type(QC_LIST);
    await this.sp.updateItem(QC_LIST, spId, {
      __metadata: { type },
      supApprovedBy: by,
      supApprovedAt: new Date().toISOString(),
    });
  }

  /** A supervisor reviewed an in-house repair and disagreed with the call.
   * Recorded for accountability only — does not reclassify or undo the
   * scan; a human still has to act on what the disagreement was about. */
  public async rejectInhouseRepair(spId: number, by: string): Promise<void> {
    const type = await this.type(QC_LIST);
    await this.sp.updateItem(QC_LIST, spId, {
      __metadata: { type },
      supRejectedBy: by,
      supRejectedAt: new Date().toISOString(),
    });
  }

  /**
   * Stamps scans as folded into a BQC5/REPAIR outbox export.
   *
   * Written only AFTER the upload succeeds — a scan wrongly marked synced is
   * a row the workbook never gets and nobody notices, whereas a scan wrongly
   * left unsynced is re-sent and merges idempotently on the VBA side. Errors
   * are swallowed per row for the same reason: a partial stamp costs a
   * duplicate row, a thrown stamp costs the whole export. See qcOutbox.ts.
   */
  public async markSynced(spIds: readonly number[]): Promise<void> {
    if (spIds.length === 0) return;
    const type = await this.type(QC_LIST);
    const syncedToWorkbookAt = new Date().toISOString();
    for (const spId of spIds) {
      try {
        await this.sp.updateItem(QC_LIST, spId, { __metadata: { type }, syncedToWorkbookAt });
      } catch (e) {
        console.warn(`[QcService] could not stamp scan ${spId} as synced — it will be re-exported`, e);
      }
    }
  }

  /** Hard-delete a scan row — the record is GONE, not history. For rows that
   * should never have existed; an ordinary mis-scan wants undoQcScan
   * instead. */
  public async deleteQcScan(spId: number): Promise<void> {
    await this.sp.deleteItem(QC_LIST, spId);
  }

  /** Hard-deletes every repair-outcome scan (any round, resolved or still
   * outstanding) — a full wipe of BQC5's repair test data, for clearing out
   * rehearsal data before a fresh test. Pass-outcome scans are untouched.
   * Returns how many rows were removed. */
  public async clearRepairScans(): Promise<number> {
    const rows = await this.listQcScans();
    const ids = rows.filter(r => r.outcome === "repair").map(r => r.spId);
    for (const id of ids) await this.deleteQcScan(id);
    return ids.length;
  }
}

export const qcService = new QcService();
